import * as Either from 'fp-ts/lib/Either';

import type { IOts, OptionTs, TaskEitherTs } from '@ps-bridge/interop';
import type { Ok } from '@ps-bridge/ok';
import * as Option from "@utils/Option";
import * as IO from "@ps-bridge/interop/io";
import * as Task from "@ps-bridge/interop/task";
import * as TaskEither from "@ps-bridge/interop/taskEither";

export const runIO = <Value>(io: IOts<Value>): Value => IO.run(io);

export const evaluateTaskEither = async <Error, Value>(
    task: TaskEitherTs<Error, Value>,
): Promise<Either.Either<Error, Value>> =>
    Task.run(
        TaskEither.fold((error: Error) => Task.pure(Either.left(error) as Either.Either<Error, Value>))(
            (value: Value) => Task.pure(Either.right(value) as Either.Either<Error, Value>),
        )(task),
    );

export type TaskEitherHandlers<Error, Value> = {
    readonly onLeft: (error: Error) => IOts<Ok>;
    readonly onRight: (value: Value) => IOts<Ok>;
};

export const runTaskEither = async <Error, Value>(
    task: TaskEitherTs<Error, Value>,
    handlers: TaskEitherHandlers<Error, Value>,
): Promise<Ok> => {
    const result = await evaluateTaskEither(task);
    if (Either.isLeft(result)) {
        return runIO(handlers.onLeft(result.left));
    }
    return runIO(handlers.onRight(result.right));
};

export type TaskEitherErrorFormatter<Error> = (error: Error) => {
    readonly message: string;
    readonly info: OptionTs<string>;
};

export const runTaskEitherWithError = async <Error, Value>(
    task: TaskEitherTs<Error, Value>,
    triggerErrorState: (message: string, info: OptionTs<string>) => IOts<Ok>,
    formatError: TaskEitherErrorFormatter<Error>,
): Promise<Either.Either<Error, Value>> => {
    const result = await evaluateTaskEither(task);
    if (Either.isLeft(result)) {
        const { message, info } = formatError(result.left);
        runIO(triggerErrorState(message, info));
    }
    return result;
};

export type DefaultTaskError = {
    readonly message: string;
    readonly info: OptionTs<string>;
};

export const defaultTaskErrorFormatter = (reason: Error): DefaultTaskError => ({
    message: reason.message,
    info: Option.none(),
});
